import { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import Sidebar from "./components/Sidebar";
import BlockIdentite from "./components/BlockIdentite";
import BlockInfosPerso from "./components/BlockInfosPerso";
import BlockEtatCivil from "./components/BlockEtatCivil";
import BlockConjoint from "./components/BlockConjoint";
import BlockEnfants from "./components/BlockEnfants";
import BlockBancaire from "./components/BlockBancaire";
import BlockUrgence from "./components/BlockUrgence";
import { Button, Flex, Input, Text } from "@fluentui/react-northstar";
import { PhoneInputStyle } from "../../common/styles";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { SCHEMA_FACTSHEET } from "../../features/addEmployees/constants/validators";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import {
    _api_get_factsheet,
    _api_post_factsheet,
    _api_put_factsheet,
} from "../../api/factsheet";
import moment from "moment";
import AlertInfo from "../../components/Alerts/Info";
import DialogExportFactsheet from "../New_Onboarding/components/TableFactsheet/DialogExportFactsheet";
import {
    CellStatus,
    STATUS_DROPDOWN,
} from "../New_Onboarding/components/TableFactsheet/TableFactsheet";
import { toast } from "sonner";
import { useAuthDetailStore } from "../../stores/useAuthStore";

export const IDENTITY_CARDS = [
    {
        key: 1,
        id: 1,
        header: "Carte nationale d'identité",
    },
    {
        key: 2,
        id: 2,
        header: "Passeport",
    },
    {
        key: 3,
        id: 3,
        header: "Titre de séjour",
    },
];

export default function CollaboratorFactsheet() {
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);

    const detail = useAuthDetailStore((state) => state.detail);

    const user_id = detail?.id;

    const methods = useForm({
        mode: "onSubmit",
        resolver: yupResolver(SCHEMA_FACTSHEET),
        defaultValues: {
            personal_infos: {
                marital_status: 1,
                marriage_date: null,
                divorce_date: null,
                couple_registration_date: null,
            },
        },
    });

    const {
        data: dataFactsheet,
        isLoading: isLoadingFactsheet,
        refetch: refetchGetFactsheet,
    } = useQuery(
        ["get_factsheet"],
        () => _api_get_factsheet({ id_user: user_id }),
        {
            refetchOnWindowFocus: false,
        }
    );

    useEffect(() => {
        if (dataFactsheet) {
            methods.reset(dataFactsheet);
        }
    }, [isLoadingFactsheet]);

    const { isLoading: isSavingData, mutate: saveFactsheet } = useMutation(
        (data) => _api_put_factsheet({ id_user: user_id, data })
    );

    const { isLoading: isSubmittingData, mutate: submitFactsheet } =
        useMutation((data) => _api_post_factsheet({ id_user: user_id, data }));

    const handleSave = () => {
        saveFactsheet(methods.getValues(), {
            onSuccess: (data) => {
                if (data?.success) {
                    toast.success("Fiche employé enregistrée avec succès");
                    refetchGetFactsheet();
                    return;
                }
                if (data?.message) {
                    toast.error(data.message);
                    return;
                }
                toast.error(
                    "Une erreur est survenue lors de l'enregistrement de la fiche employé"
                );
                return;
            },
        });
    };

    const handleSubmitForm = methods.handleSubmit((data) => {
        submitFactsheet(data, {
            onSuccess: (data) => {
                setIsEditing(false);
                if (data?.success) {
                    toast.success("Fiche employé soumise avec succès");
                    refetchGetFactsheet();
                    return;
                }
                toast.error(
                    "Une erreur est survenue lors de la soumission de la fiche employé"
                );
                return;
            },
        });
    });

    const goBack = () => {
        return navigate(-1);
    };

    const handleEditFactsheet = () => {
        setIsEditing(true);
        return;
    };

    const task_created_by = dataFactsheet?.completeness?.task_created_by ?? "";
    const task_due_date = dataFactsheet?.completeness?.task_due_date
        ? moment(dataFactsheet.completeness.task_due_date).format("DD/MM/YYYY")
        : "";

    const status = dataFactsheet?.completeness?.status;
    const badgeStatus = STATUS_DROPDOWN.find((s) => s.id === status);
    const rejected_reason = dataFactsheet?.completeness?.rejected_reason ?? "";
    const rejected_date = dataFactsheet?.completeness?.rejected_date ?? "";
    const rejected_by = dataFactsheet?.completeness?.rejected_by ?? "";
    const email_perso = dataFactsheet?.identity?.email ?? "";
    const submitted_date = dataFactsheet?.completeness?.submitted_date ?? "";
    const approval_date = dataFactsheet?.completeness?.approval_date ?? "";
    const approved_by = dataFactsheet?.completeness?.approved_by ?? "";

    return (
        <>
            <FormProvider {...methods}>
                <form>
                    <PageWrapper>
                        <Sidebar
                            task_created_by={task_created_by}
                            task_due_date={task_due_date}
                            status={status}
                            approval_date={approval_date}
                        />
                        {/* validée */}
                        {status === 1 && !isEditing && (
                            <MainContent>
                                <SectionHeader>
                                    <SectionTitle>Fiche employé</SectionTitle>
                                </SectionHeader>
                                <Flex>
                                    <AlertInfo
                                        title={
                                            "Votre fiche employé a été validée"
                                        }
                                        content={
                                            <Flex column gap="gap.smaller">
                                                <Text>
                                                    Votre responsable RH a
                                                    accepté votre fiche le{" "}
                                                    <b>
                                                        {approval_date.replace(
                                                            " ",
                                                            " à "
                                                        )}
                                                    </b>
                                                    . Vos informations sont
                                                    enregistrées dans votre
                                                    profil Yeetch.
                                                </Text>
                                                <Text
                                                    content={`Un email de confirmation vous a été envoyé ainsi qu'à votre RH.`}
                                                />
                                            </Flex>
                                        }
                                        success
                                    />
                                </Flex>
                                <Block column gap="gap.large">
                                    <Flex column gap="gap.small">
                                        <Flex gap="gap.small" vAlign="center">
                                            <CellStatus status={badgeStatus} />
                                            <STextTitleSuccess content="Actions sur ma fiche employé" />
                                        </Flex>
                                        <Flex>
                                            <STextDescription>
                                                Soumise le{" "}
                                                <b>
                                                    {submitted_date?.replace(
                                                        " ",
                                                        " à "
                                                    )}
                                                </b>{" "}
                                                · Validée le{" "}
                                                <b>
                                                    {approval_date?.replace(
                                                        " ",
                                                        " à "
                                                    )}
                                                </b>{" "}
                                                par <b>{approved_by}</b> (RH)
                                            </STextDescription>
                                        </Flex>
                                        <DialogExportFactsheet
                                            id_user={user_id}
                                            buttonContent="⬇ Télécharger ma fiche"
                                        />
                                    </Flex>
                                </Block>
                            </MainContent>
                        )}
                        {/* soumise */}
                        {status === 2 && (
                            <MainContent>
                                <SectionHeader>
                                    <SectionTitle>Fiche employé</SectionTitle>
                                </SectionHeader>
                                <Flex>
                                    <AlertInfo
                                        title={
                                            "Votre fiche employé a été soumise"
                                        }
                                        content={
                                            <Flex column gap="gap.smaller">
                                                <Text
                                                    content={`Votre fiche a bien été transmise à votre responsable RH. Elle est en cours de vérification, vous serez notifié(e) une fois qu'elle aura été traitée.`}
                                                />
                                                <Text
                                                    content={`Vous ne pouvez plus modifier vos informations tant que la fiche est en cours de vérification.`}
                                                />
                                            </Flex>
                                        }
                                        warning
                                    />
                                </Flex>
                                <Block column gap="gap.large">
                                    <Flex column gap="gap.small">
                                        <Flex gap="gap.small" vAlign="center">
                                            <CellStatus status={badgeStatus} />
                                            <STextTitleSuccess content="Actions sur ma fiche employé" />
                                        </Flex>
                                        <Flex gap="gap.smaller" vAlign="center">
                                            <STextDescription
                                                content={`Soumise le`}
                                            />
                                            <STextDescription
                                                content={submitted_date?.replace(
                                                    " ",
                                                    " à "
                                                )}
                                                weight="bold"
                                            />
                                        </Flex>
                                        <DialogExportFactsheet
                                            id_user={user_id}
                                            buttonContent="⬇ Télécharger ma fiche"
                                        />
                                    </Flex>
                                </Block>
                            </MainContent>
                        )}
                        {/* refusée */}
                        {status === 3 && !isEditing && (
                            <MainContent>
                                <SectionHeader>
                                    <SectionTitle>Fiche employé</SectionTitle>
                                </SectionHeader>
                                <Flex>
                                    <AlertInfo
                                        title={
                                            "Votre fiche employé a été refusée"
                                        }
                                        content={
                                            <Flex column gap="gap.smaller">
                                                <Flex gap="gap.smaller">
                                                    <Text
                                                        content={`Votre responsable RH a refusé votre fiche le `}
                                                    />
                                                    <Text
                                                        content={rejected_date}
                                                        weight="bold"
                                                    />
                                                </Flex>
                                                <Text
                                                    content={`Merci de corriger les informations indiquées et de soumettre à nouveau.`}
                                                />
                                                <Text
                                                    content={
                                                        <>
                                                            <Text
                                                                content={`Un email récapitulatif vous a été envoyé à `}
                                                            />

                                                            <Text
                                                                content={
                                                                    email_perso
                                                                }
                                                                weight="bold"
                                                            />
                                                        </>
                                                    }
                                                />
                                            </Flex>
                                        }
                                        error
                                    />
                                </Flex>
                                <Block column gap="gap.large">
                                    <Flex column gap="gap.small">
                                        <Flex gap="gap.small" vAlign="center">
                                            <CellStatus status={badgeStatus} />
                                            <STextTitleSuccess content="Compléter votre fiche employé" />
                                        </Flex>
                                        <Flex column gap="gap.small">
                                            <Flex
                                                vAlign="center"
                                                gap="gap.smaller"
                                            >
                                                <STextDescription
                                                    content={`Refusée le ${rejected_date} par ${rejected_by} `}
                                                />
                                                <STextDescription
                                                    content={`·`}
                                                />
                                                <STextDescription
                                                    content={`Nouvelle échéance : ${task_due_date}`}
                                                    weight="bold"
                                                    style={{
                                                        color: "var(--red)",
                                                    }}
                                                />
                                            </Flex>
                                            <AlertInfo
                                                title={
                                                    "💬 Commentaire de votre responsable RH"
                                                }
                                                content={
                                                    <Flex
                                                        column
                                                        gap="gap.smaller"
                                                    >
                                                        <STextComment
                                                            content={`"${rejected_reason}."`}
                                                        />
                                                        <STextLegend
                                                            content={`${rejected_by} — RH · ${rejected_date}`}
                                                        />
                                                    </Flex>
                                                }
                                                error
                                            />
                                        </Flex>
                                        <Flex gap="gap.small">
                                            <SButton
                                                content="Corriger ma fiche"
                                                flat
                                                danger
                                                type="button"
                                                onClick={handleEditFactsheet}
                                            />
                                            <DialogExportFactsheet
                                                id_user={user_id}
                                                buttonContent="⬇ Télécharger ma fiche"
                                            />
                                        </Flex>
                                    </Flex>
                                </Block>
                            </MainContent>
                        )}
                        {/* incomplete */}
                        {(status === 0 || isEditing) && (
                            <MainContent>
                                <SectionHeader>
                                    <SectionCircle>✎</SectionCircle>
                                    <SectionTitle>
                                        Ma fiche de renseignement{" "}
                                        <BadgeCollab>
                                            À compléter par vous
                                        </BadgeCollab>
                                    </SectionTitle>
                                </SectionHeader>

                                <BlockIdentite />
                                <BlockInfosPerso />
                                <BlockEtatCivil />
                                <BlockConjoint />
                                <BlockEnfants />
                                <BlockBancaire />
                                <BlockUrgence />

                                <Footer>
                                    <BtnCancel type="button" onClick={goBack}>
                                        Annuler
                                    </BtnCancel>
                                    <Flex vAlign="center" gap="gap.medium">
                                        <BtnSave
                                            type="button"
                                            onClick={handleSave}
                                            loading={isSavingData}
                                            secondary
                                            content={
                                                "Enregistrer mes informations"
                                            }
                                            flat
                                        />
                                        <BtnSave
                                            type="button"
                                            onClick={handleSubmitForm}
                                            loading={isSubmittingData}
                                            content={"Soumettre ma fiche"}
                                        />
                                    </Flex>
                                </Footer>
                            </MainContent>
                        )}
                    </PageWrapper>
                </form>
            </FormProvider>
        </>
    );
}

const STextLegend = styled(Text)`
    font-size: 11px;
    color: var(--text-tert);
    margin-top: 6px;
`;

const STextComment = styled(Text)`
    font-size: 13px;
    color: var(--text);
    line-height: 1.6;
    font-style: italic;
`;

const SButton = styled(Button)`
    background: var(--red);
    color: white;
    font-size: 12px;
    padding: 8px 16px;
`;
const STextDescription = styled(Text)`
    font-size: 12px;
    color: var(--text-tert);
    margin-bottom: 12px;
`;
const STextTitleSuccess = styled(Text)`
    font-size: 14px;
    font-weight: 600;
`;

/* ─── Layout ─────────────────────────────────────────────────── */

const PageWrapper = styled.div`
    display: flex;
    min-height: 100vh;
    background: var(--bg);
    font-size: 14px;
    color: var(--text);
`;

const MainContent = styled.div`
    flex: 1;
    padding: 28px 32px;
    max-width: 860px;
    overflow-y: auto;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 22px;
    flex-wrap: wrap;
`;

const SectionCircle = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: var(--green);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    font-weight: 700;
    flex-shrink: 0;
`;

const SectionTitle = styled.div`
    font-size: 19px;
    font-weight: 700;
    color: var(--text);
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const Footer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 24px;
    padding-top: 18px;
    border-top: 1px solid var(--border);
    margin-bottom: 32px;
`;

const BtnCancel = styled.button`
    font-size: 13px;
    color: var(--red);
    text-decoration: underline;
    cursor: pointer;
    background: none;
    border: none;
    font-family: inherit;
`;

const BtnSave = styled(Button)`
    font-size: 13px;
    padding: 9px 20px;
    border-radius: var(--radius-sm);
    background: var(--green);
    color: white;
    border: none;
    cursor: pointer;
    font-weight: 600;
    font-family: inherit;
    transition: background 0.12s;

    ${({ secondary }) =>
        secondary &&
        css`
            font-size: 13px;
            padding: 9px 18px;
            border: 1px solid var(--border-mid);
            border-radius: var(--radius-sm);
            background: var(--bg-card);
            color: var(--text);
            transition: all 0.12s;
        `}
    &:hover {
        background: ${({ secondary }) =>
            secondary ? "var(--bg-card)" : "var(--green-dark)"};
        ${({ secondary }) =>
            secondary &&
            css`
                border-color: var(--purple);
                color: var(--purple);
            `}
    }
`;

/* ─── Shared styled-components (exported for child blocks) ────── */

export const BadgeCollab = styled.span`
    display: inline-flex;
    align-items: center;
    font-size: 10px;
    padding: 2px 8px;
    border-radius: 20px;
    font-weight: 600;
    background: var(--green-light);
    color: var(--green-dark);
    white-space: nowrap;
`;

export const BadgeRh = styled.span`
    display: inline-flex;
    align-items: center;
    font-size: 10px;
    padding: 2px 8px;
    border-radius: 20px;
    font-weight: 600;
    background: var(--blue-light);
    color: var(--blue);
    white-space: nowrap;
`;

export const BadgeFrozen = styled.span`
    display: inline-flex;
    align-items: center;
    font-size: 10px;
    padding: 2px 8px;
    border-radius: 20px;
    font-weight: 600;
    background: var(--bg-secondary);
    color: var(--text-tert);
    border: 1px solid var(--border);
    white-space: nowrap;
`;

export const Block = styled.div`
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    padding: 20px 22px;
    margin-bottom: 14px;
    box-shadow: var(--shadow);
`;

export const BlockFrozen = styled(Block)`
    border-color: var(--border-mid);
    background: var(--bg-secondary);
`;

export const BlockCollab = styled(Block)`
    border-color: var(--green-mid);
`;

export const BlockTitle = styled.div`
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--text-sec);
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    gap: 7px;
    flex-wrap: wrap;
`;

export const BlockTitleCollab = styled(BlockTitle)`
    color: var(--green-dark);
`;

export const FieldGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 13px;
`;

export const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;

    & > label {
        font-size: 11px;
        color: var(--text-sec);
        font-weight: 500;
        display: flex;
        align-items: center;
        gap: 4px;
        flex-wrap: wrap;
    }
`;

export const FieldFull = styled(Field)`
    grid-column: 1 / -1;
`;

export const Req = styled.span`
    color: var(--red);
    font-size: 12px;
`;

export const OptLabel = styled.span`
    font-size: 10px;
    color: var(--text-tert);
    font-style: italic;
    font-weight: 400;
`;

export const FieldInput = styled.div`
    height: 36px;
    background: var(--bg-secondary);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 0 11px;
    font-size: 13px;
    color: var(--text-tert);
    display: flex;
    align-items: center;
`;

export const FieldInputFrozen = styled(FieldInput)`
    color: var(--text);
    cursor: not-allowed;
`;

export const FieldInputFrozenEmpty = styled(FieldInput)`
    border: 1px dashed var(--border-mid);
`;

export const FieldSelect = styled.div`
    height: 36px;
    background: var(--bg-secondary);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 0 11px;
    font-size: 13px;
    color: var(--text-tert);
    display: flex;
    align-items: center;
    justify-content: space-between;

    &::after {
        content: "▾";
        font-size: 10px;
        color: var(--text-tert);
    }
`;

export const FieldSelectReal = styled.select`
    height: 36px;
    width: 100%;
    background: var(--bg-secondary);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 0 28px 0 11px;
    font-size: 13px;
    color: var(--text);
    font-family: inherit;
    cursor: pointer;
    appearance: none;
    outline: none;
    transition: border-color 0.12s;

    &:focus {
        border-color: var(--purple);
        background-color: white;
    }
`;

export const InfoNoteGray = styled.div`
    background: var(--bg-secondary);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 10px 14px;
    font-size: 12px;
    color: var(--text-sec);
    margin-bottom: 14px;
    line-height: 1.6;
`;

export const TagGroup = styled.div`
    display: flex;
    gap: 7px;
    flex-wrap: wrap;
    margin-top: 4px;
`;

export const Tag = styled.div`
    padding: 5px 14px;
    border-radius: 20px;
    font-size: 12px;
    cursor: pointer;
    border: 1px solid
        ${({ active }) => (active ? "var(--purple)" : "var(--border)")};
    background: ${({ active }) =>
        active ? "var(--purple)" : "var(--bg-secondary)"};
    color: ${({ active }) => (active ? "white" : "var(--text-sec)")};
    font-weight: 500;
    transition: all 0.12s;
    user-select: none;
    display: flex;
    align-items: center;
    gap: 5px;

    &:hover {
        border-color: var(--purple);
        color: ${({ active }) => (active ? "white" : "var(--purple)")};
    }
`;

export const Footnote = styled.div`
    font-size: 11px;
    color: var(--text-tert);
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px dashed var(--border);
    line-height: 1.6;
`;

export const SInput = styled(Input)`
    input {
        height: 36px;
        background: var(--bg-secondary);
        border: 1px solid var(--border);
        border-radius: var(--radius-sm);
        font-size: 13px;
        color: var(--text);
        &[disabled] {
            cursor: not-allowed;
        }
        &[aria-invalid="true"] {
            border-color: var(--red);
        }
    }
`;

export const SPhoneInputStyle = styled(PhoneInputStyle)`
    height: 36px;
    background: var(--bg-secondary) !important;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    font-size: 13px;
    color: var(--text);
    input {
        background-color: transparent;
        &[disabled] {
            cursor: not-allowed;
        }
        &[aria-invalid="true"] {
            border-color: var(--red);
        }
    }
`;
